import React, { useState, useEffect } from 'react';
import { Form, Input, Select, InputNumber, DatePicker, Button, Card, message, Space } from 'antd';
import deferredRevenueService from '../../../../services/deferredRevenueService';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

const DeferredRevenueForm: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [referenceType, setReferenceType] = useState<string>('MANUAL_ENTRY');

  useEffect(() => {
    fetchAccounts();
    fetchInvoices();
  }, []);

  const fetchAccounts = async () => {
    try {
      // Fetch accounts from API
      const accountService = await import('../../../../services/accountService');
      const allAccounts = await accountService.default.getAccounts();
      console.log('Fetched accounts:', allAccounts.length, 'accounts');
      console.log('Sample account:', allAccounts[0]);
      console.log('Sample account ID:', allAccounts[0]?.id, 'Type:', typeof allAccounts[0]?.id);

      // Filter accounts by type
      const liabilityAccounts = allAccounts.filter((acc) => acc.accountType === 'LIABILITY');
      const revenueAccountsList = allAccounts.filter((acc) => acc.accountType === 'REVENUE');
      console.log('Liability accounts:', liabilityAccounts.length);
      console.log('Revenue accounts:', revenueAccountsList.length);

      setAccounts(allAccounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      // Fallback to empty array if fetch fails
      setAccounts([]);
    }
  };

  const fetchInvoices = async () => {
    // TODO: Fetch unpaid/sent invoices from API
    // For now, using mock data
    setInvoices([
      { id: '1', invoiceNo: 'INV-2025-001', totalAmount: 12000 }
    ]);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      console.log('Form values received:', values);
      console.log('deferredAccountId from form:', values.deferredAccountId, 'Type:', typeof values.deferredAccountId);
      console.log('revenueAccountId from form:', values.revenueAccountId, 'Type:', typeof values.revenueAccountId);

      const data = {
        name: values.name,
        referenceType: values.referenceType,
        referenceId: values.referenceId,
        deferredAccountId: values.deferredAccountId,
        revenueAccountId: values.revenueAccountId,
        totalAmount: values.totalAmount,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
        recognitionMethod: values.recognitionMethod || 'STRAIGHT_LINE',
        frequency: values.frequency || 'MONTHLY'
      };

      console.log('Submitting deferred revenue data:', data);
      console.log('Account IDs in data:', {
        deferredAccountId: data.deferredAccountId,
        revenueAccountId: data.revenueAccountId
      });

      const response = await deferredRevenueService.create(data);

      console.log('API Response:', response);

      // Check if response has id (successful creation) or success property
      if (response?.id || response?.success || response?.data) {
        message.success('Deferred revenue created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response?.message || 'Failed to create deferred revenue');
      }
    } catch (error: any) {
      console.error('Failed to create deferred revenue:', error);
      const errorMessage = error.response?.data?.errors
        ? JSON.stringify(error.response.data.errors, null, 2)
        : error.message || 'Failed to create deferred revenue';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceChange = (invoiceId: string) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    if (invoice) {
      form.setFieldsValue({
        name: `Deferred Revenue - ${invoice.invoiceNo}`,
        totalAmount: invoice.totalAmount
      });
    }
  };

  const deferredAccounts = accounts.filter((acc) => acc.accountType === 'LIABILITY');
  const revenueAccounts = accounts.filter((acc) => acc.accountType === 'REVENUE');

  return (
    <Card title="Create Deferred Revenue">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          referenceType: 'MANUAL_ENTRY',
          recognitionMethod: 'STRAIGHT_LINE',
          frequency: 'MONTHLY'
        }}
      >
        <Form.Item
          name="referenceType"
          label="Reference Type"
          rules={[{ required: true }]}
        >
          <Select onChange={setReferenceType}>
            <Option value="MANUAL_ENTRY">Manual Entry</Option>
            <Option value="INVOICE">Invoice</Option>
          </Select>
        </Form.Item>

        {referenceType === 'INVOICE' && (
          <Form.Item
            name="referenceId"
            label="Invoice"
            rules={[{ required: true, message: 'Please select an invoice' }]}
          >
            <Select
              placeholder="Select invoice"
              onChange={handleInvoiceChange}
              showSearch
              optionFilterProp="children"
            >
              {invoices.map((inv) => (
                <Option key={inv.id} value={inv.id}>
                  {inv.invoiceNo} - ${inv.totalAmount.toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="e.g., Annual Subscription - Customer ABC" />
        </Form.Item>

        <Form.Item
          name="deferredAccountId"
          label="Deferred Revenue Account (Liability)"
          rules={[{ required: true, message: 'Please select a deferred account' }]}
        >
          <Select placeholder="Select deferred account" showSearch optionFilterProp="children">
            {deferredAccounts.map((acc) => (
              <Option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="revenueAccountId"
          label="Revenue Account"
          rules={[{ required: true, message: 'Please select a revenue account' }]}
        >
          <Select placeholder="Select revenue account" showSearch optionFilterProp="children">
            {revenueAccounts.map((acc) => (
              <Option key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="totalAmount"
          label="Total Amount"
          rules={[
            { required: true, message: 'Please enter the total amount' },
            { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Enter total amount"
            precision={2}
            min={0.01}
            prefix="$"
          />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="Recognition Period"
          rules={[{ required: true, message: 'Please select the recognition period' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />
        </Form.Item>

        <Form.Item
          name="frequency"
          label="Recognition Frequency"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="MONTHLY">Monthly</Option>
            <Option value="QUARTERLY">Quarterly</Option>
            <Option value="ANNUAL">Annual</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="recognitionMethod"
          label="Recognition Method"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value="STRAIGHT_LINE">Straight-Line (Equal Distribution)</Option>
            <Option value="CUSTOM">Custom (Manual per Period)</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Deferred Revenue
            </Button>
            <Button onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default DeferredRevenueForm;
